import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useActiveModules } from './registry';
import { buildNavigation, type NavigationEntry, type ResolvedNavNode } from './navigation';
import type { NavNode } from './types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * The person's navigation (sidebar and the „Seiten“ hits of the search):
 * menu entries from the database plus the nav nodes of the active modules.
 */
export function useNavigationTree(): ResolvedNavNode[] {
  const { hasMenuAccess, user } = useAuth();
  const modules = useActiveModules();

  const { data: entries = [] } = useQuery({
    queryKey: ['navigation', user?.id],
    queryFn: async () => {
      const res = await axios.get<{ entries: NavigationEntry[] }>(`${API_URL}/menu-permissions/navigation`, { withCredentials: true });
      return res.data.entries;
    },
    enabled: !!user,
  });

  const nodes = useMemo(() => {
    const map = new Map<string, NavNode>();
    for (const mod of modules) for (const node of mod.nav ?? []) map.set(node.menuKey, node);
    return map;
  }, [modules]);

  return useMemo(() => buildNavigation(entries, nodes, hasMenuAccess), [entries, nodes, hasMenuAccess]);
}
